import { ROUTER_API } from 'app/constants/api.constant';
import { TagModelOutDTO } from 'app/fuse-layouts/layout1/type';
import BaseApi from 'app/services/api/base.api';
import { LBOrderDTO, ReceiptModelInDTO } from '../../scanner/types';
import {
  PairingCodeDTO,
  PosDeviceDTO,
  PosMerchantAccountDTO,
  PosProductDTO,
  SalesReceptionStatusDTO,
  StoreDTO,
  StoreSettingsDTO,
} from '../type';

export const storesApiV2 = new BaseApi({ baseURL: ROUTER_API, tokenKey: 'token', timeout: 60000 });
export const storesProvider = new BaseApi({
  baseURL: `${ROUTER_API}/providers/core/v1`,
  tokenKey: 'token',
  timeout: 60000,
});

export const getStoresApi = async () => {
  const response = await storesApiV2.api.get('api/v2/stores/owned');
  const stores: StoreDTO[] = await response.data;

  return stores || [];
};

export const createStoreApi = async (request: StoreDTO) => {
  const response = await storesApiV2.api.post('/api/v2/stores', request);
  return response.data;
};

export const updateStoreApi = async (storeId: string, store: Partial<StoreDTO>) => {
  const response = await storesApiV2.api.put(`/api/v2/stores/${storeId}`, store);
  const { data } = response;

  return data;
};

export const removeStoreApi = async (storeId: string) => {
  const response = await storesApiV2.api.patch(`/api/v2/stores/${storeId}/deactivate`);
  return response.data;
};

export const getPosMerchantAccountApi = async (storeId: string) => {
  const response = await storesProvider.api.get<PosMerchantAccountDTO | null>(`/pos-merchant-accounts/search`, {
    params: {
      storeId,
    },
  });
  return response.data;
};

export const createPosMerchantAccountApi = async (posMerchantAccount: Partial<PosMerchantAccountDTO>) => {
  const response = await storesProvider.api.post<PosMerchantAccountDTO>(`/pos-merchant-accounts`, posMerchantAccount);
  return response.data;
};

export const updatePosMerchantAccountApi = async (posMerchantAccount: Partial<PosMerchantAccountDTO>) => {
  const response = await storesProvider.api.put<PosMerchantAccountDTO>(
    `/pos-merchant-accounts/${posMerchantAccount.id}`,
    posMerchantAccount,
  );
  return response.data;
};

export const getPosProductsApi = async (storeId: string, productName: string, includeExtras = false) => {
  const response = await storesProvider.api.get<PosProductDTO[]>(`/stores/${storeId}/products`, {
    params: {
      productName,
      includeExtras,
    },
  });
  return response.data;
};

export const getPosExtrasApi = async (storeId: string, extraName: string, includeProducts = false) => {
  const response = await storesProvider.api.get<PosProductDTO[]>(`/stores/${storeId}/extras`, {
    params: {
      extraName,
      includeProducts,
    },
  });
  return response.data;
};

export const getPosOrderInformationApi = async (orderId: string) => {
  const url = `/orders/pos/${orderId}`;
  const { data } = await storesProvider.api.get<ReceiptModelInDTO>(url);
  return data;
};

export const getLBOrderInformationApi = async (orderId: string) => {
  const url = `/api/v2/orders/lb/${orderId}`;
  const { data } = await storesApiV2.api.get<LBOrderDTO>(url);
  return data;
};

export const injectPointsOneTagApi = async (tagId: string, amount: number) => {
  const response = await storesApiV2.api.post<TagModelOutDTO>(`api/v2/tags/${tagId}/inject-points`, { amount });
  return response.data;
};

export const injectPointsMultipleTagsApi = async (tagIds: string[], amount: number) => {
  const response = await storesApiV2.api.post<LBOrderDTO>(`api/v2/tags/inject-points`, { amount, tagIds });
  return response.data;
};

export const resetPointsInTagApi = async (tagId: string) => {
  const response = await storesApiV2.api.post<TagModelOutDTO>(`api/v2/tags/${tagId}/reset-points`);
  return response.data;
};

export const patchStoreSettingsApi = async (storeId: string, settings: Partial<StoreSettingsDTO>) => {
  const response = await storesApiV2.api.patch<StoreSettingsDTO>(`/api/v2/stores/${storeId}/settings`, settings);
  return response.data;
};

export const getStoreSalesReceptionStatusApi = async (storeId: string) => {
  const response = await storesApiV2.api.get<SalesReceptionStatusDTO>(
    `/api/v2/stores/${storeId}/sales-reception-status`,
  );
  return response.data;
};

export const clearStoreSalesReceptionErrorsApi = async (storeId: string) => {
  const response = await storesApiV2.api.post<SalesReceptionStatusDTO>(
    `/api/v2/stores/${storeId}/sales-reception-status/clear-errors`,
  );
  return response.data;
};

export const patchStoreApi = async (storeId: string, store: Partial<StoreDTO>) => {
  const response = await storesApiV2.api.patch<StoreDTO>(`/api/v2/stores/${storeId}/billing`, store);
  return response.data;
};

export const getPosDevicesApi = async (storeId: string) => {
  const { data } = await storesApiV2.api.get<PosDeviceDTO[]>('/api/v2/pos-devices', { params: { storeId } });
  return data || [];
};

export const createPairingCodeApi = async (payload: { storeId: string; label: string; tagId: string }) => {
  const { data } = await storesApiV2.api.post<PairingCodeDTO>('/api/v2/pos-devices/pairing-codes', payload);
  return data;
};

export const regeneratePairingCodeApi = async (deviceId: string, tagId?: string) => {
  const { data } = await storesApiV2.api.post<PairingCodeDTO>(`/api/v2/pos-devices/${deviceId}/pairing-code`, {
    tagId: tagId ?? null,
  });
  return data;
};

export const revokePosDeviceApi = async (deviceId: string) => {
  await storesApiV2.api.delete(`/api/v2/pos-devices/${deviceId}`);
};

export type KeziaCredentialsDTO = {
  serverUrl: string;
  clientId: string;
  clientSecret: string;
};

export type EtposConnectionStatus =
  | 'CONNECTED'
  | 'INCOMPLETE'
  | 'UNKNOWN_PROXY'
  | 'RELAY_UNAVAILABLE'
  | 'TILL_UNREACHABLE'
  | 'KEYS_REFUSED';

export type EtposAccessDTO = { proxyId: string; publicKey: string; privateKey: string };

export const checkEtposConnectionApi = async (storeId: string, access: EtposAccessDTO) => {
  const response = await storesProvider.api.post<{ status: EtposConnectionStatus }>(
    `/stores/${storeId}/etpos/connection-check`,
    access,
  );
  return response.data.status;
};

export const getKeziaCredentialsApi = async (storeId: string) => {
  const response = await storesProvider.api.get<KeziaCredentialsDTO>(`/stores/${storeId}/kezia-credentials`);
  return response.data;
};
